import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import NumberFormat from 'react-number-format';
import __ from 'lodash';
import { FormControl, InputLabel, TextField } from "@material-ui/core";
import { isBlank } from '../utils';

const MaskedNumber = forwardRef((props, ref) => {
  const inputRef = useRef();
  const [dataGridValues, setDataGridValues] = useState({value: props.value, floatValue: props.value});
  //see https://github.com/s-yadav/react-number-format#values-object for prop details
  const controlProps = {
    isdatagrid: false,
    isNumericString: false,
    thousandSeparator: ',',
    decimalSeparator: '.',
    thousandsGroupStyle: 'thousand',
    displayType: 'input', //can also be text to make readonly render
    decimalScale: undefined, //number of decimal places
    fixedDecimalScale: false, // show the trailing zeroes
    value: null,
    defaultValue: null,
    type: 'text', //tel or password supported
    format: undefined, //can use # where a number is to be entered.
    mask: '',
    prefix: undefined,
    suffix: undefined,
    readOnly: false,
    ...__.omit(props, ['onChange', 'inputProps', 'controlClassName', 'className'])
  }
  if (isNaN(props.value)) {
    controlProps.value = "";
  }

  const numberFormatAllowedProps = [
    "thousandSeparator", 
    "decimalSeparator", 
    "thousandsGroupStyle", 
    "displayType",
    "decimalScale",
    "fixedDecimalScale",
    "value",
    "allowNegative",
    "allowEmptyFormatting",
    "allowLeadingZeros",
    "prefix",
    "suffix",
    "defaultValue",
    "isNumericString",
    "displayType",
    "type",
    "format",
    "removeFormatting",
    "mask",
    "customInput",
    "onValueChange",
    "isAllowed",
    "renderText",
    "getInputRef",
    "allowedDecimalSeparators",
    "customNumerals",
    "style",
    "placeHolder",
  ]

  const [oldProps, setOldProps] = useState(__.pick(props, ['value', 'defaultValue']));

  const [fieldValue, setFieldValue] = useState(controlProps.isdatagrid && controlProps.displayType === "input" ? dataGridValues.value : controlProps.value===null?'':controlProps.value);

  if (JSON.stringify(oldProps) !== JSON.stringify(__.pick(props, ['value', 'defaultValue']))) {
    setOldProps(__.pick(props, ['value', 'defaultValue']));
    setFieldValue(controlProps.isdatagrid && controlProps.displayType === "input" ? dataGridValues.value : controlProps.value===null?'':controlProps.value);
  }

  const withValueCap = (inputObj)=>{
    if (controlProps.valueCap) {
      const { value } = inputObj;
      if (value <= controlProps.valueCap) return true;
      return false;
    }
    return true;
  }

  const handleValueChange = (values, sourceInfo) => {
    const { formattedValue, value, floatValue } = values;
    setDataGridValues({...values})
    if (controlProps.displayType === "input" && props.onChange && (!props.ignorePropChanges || sourceInfo?.source !== 'prop')) {
      if (controlProps.isNumericString) {
        props.onChange(value);
        setFieldValue(value);
      } else {
        props.onChange(floatValue);
        setFieldValue(floatValue);
      }
    }    
  }

  /* Component Editor Lifecycle methods for the AG Data Grid*/
  useImperativeHandle(ref, () => {
    const { value, floatValue } = dataGridValues === undefined ? {} : dataGridValues;

    return {
      // the final value to send to the grid, on completion of editing
      getValue() {
        if (controlProps.displayType === "input") {
          if (controlProps.isNumericString) {
            return value;
          } else {
            return floatValue;
          }
        }
      },
      // Gets called once before editing starts, to give editor a chance to
      // cancel the editing before it even starts.
      isCancelBeforeStart() {
        return false;
      },

      // Gets called once when editing is finished (eg if enter is pressed).
      // If you return true, then the result of the edit will be ignored.
      isCancelAfterEnd() {
        return false;
      },

      afterGuiAttached () {
        inputRef.current.focus();
        inputRef.current.select()
      }
    }
  });

  const classNames = [props.className];
  if (props.readOnly) {
    classNames.push('readOnly');
  }
  if (controlProps.displayType==="input") {
    classNames.push("MuiInputBase-root"); 
    classNames.push("MuiInput-root");
    classNames.push("MuiInput-underline");
    classNames.push("MuiInputBase-formControl"); 
    classNames.push("MuiInput-formControl");
  } else if (controlProps.displayType === "text" && props.label) {
    classNames.push("MuiInputBase-root"); 
    classNames.push("MuiInput-root");
    classNames.push("MuiInputBase-formControl"); 
    classNames.push("MuiInput-formControl");
  }

  const className = classNames.join(" ");

  const controlClassNames = [props.controlClassName];
  if (props.readOnly) {
    controlClassNames.push('readOnly');
  }
  if (controlProps.displayType==="input") {
    controlClassNames.push("MuiInputBase-input"); 
    controlClassNames.push("MuiInput-input");
  }

  const controlClassName = controlClassNames.join(" ");

  let labelProps = {...props.inputLabelProps};
  if (props.label && (!isBlank(fieldValue) || !__.has(props?.inputLabelProps, ["shrink"]))) {
    labelProps = {shrink: !isBlank(fieldValue)}
  }

  if (controlProps.isdatagrid && controlProps.displayType === "input") {
    return (
      <NumberFormat
        key={`${props.id}`}
        id={`${props.id}`}
        labelId={`${props.id}-label`}
        {...__.pick(controlProps, numberFormatAllowedProps)}
        className={controlClassName}
        value={dataGridValues.value}
        onValueChange={handleValueChange}
        getInputRef={inputRef}
        readOnly={controlProps.readOnly}
        disabled={controlProps.disabled}
        isAllowed={withValueCap}
      />
    )
  }

  if (props.customInput) {
    return (
      <FormControl fullWidth={__.has(props,['fullWidth'])} style={{...props.containerStyle}}>
        <NumberFormat 
          key={`${props.id}`} 
          id={`${props.id}`}
          labelId={`${props.id}-label`}
          {...__.pick(controlProps, [...numberFormatAllowedProps, 'label', 'InputProps', 'InputLabelProps'])}
          className={controlClassName}
          value={fieldValue}
          onValueChange={handleValueChange}
          getInputRef={inputRef}
          readOnly={controlProps.readOnly}
          disabled={controlProps.disabled}
          isAllowed={withValueCap}
        />
      </FormControl>
    )
  }
  return (
    <FormControl fullWidth={__.has(props,['fullWidth'])} style={{...props.containerStyle}}>
      {props.label?
        <InputLabel 
          style={{position: "absolute"}} 
          key={`${props.id}-label`} 
          id={`${props.id}-label`}
          disabled={controlProps.disabled}
          {...labelProps}
        >
          {props.label}
        </InputLabel>
        :<></>
      }
      <div className={className} style={{display: "inline-block"}}>
        <NumberFormat 
          key={`${props.id}`} 
          id={`${props.id}`}
          labelId={`${props.id}-label`}
          {...__.pick(controlProps, numberFormatAllowedProps)}
          className={controlClassName}
          value={fieldValue}
          onValueChange={handleValueChange}
          getInputRef={inputRef}
          readOnly={controlProps.readOnly}
          disabled={controlProps.disabled}
          isAllowed={withValueCap}
        />
      </div>
    </FormControl>
  )
})

export default MaskedNumber;